// src/pages/DataProtectionSchools.js
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import SchoolIcon from "@mui/icons-material/School";
import SecurityIcon from "@mui/icons-material/Security";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AssessmentIcon from "@mui/icons-material/Assessment";
import "aos/dist/aos.css";
import AOS from "aos";

const DataProtectionSchools = () => {
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 1000 });
    AOS.refresh();
  }, []);

  return (
    <div
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
        fontFamily: "'Baloo Bhaijaan 2', cursive",
        color: "white",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Header */}
      <header
        style={{
          width: "100%",
          padding: "10px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.5)",
        }}
      >
        <img
          src={require("../assets/images/logo_name.PNG")}
          alt="YourStoryWorld Logo"
          style={{
            height: "40px",
            cursor: "pointer",
          }}
          onClick={() => navigate("/")}
        />
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <Button color="inherit" onClick={() => navigate("/")}>
            Home
          </Button>
          <Button color="inherit" onClick={() => navigate("/schools")}>
            For Schools
          </Button>
          <Button color="inherit" onClick={() => navigate("/login")}>
            Sign In
          </Button>
          <Button color="inherit" onClick={() => navigate("/contact")}>
            Contact
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main
        style={{
          background: "rgba(255, 255, 255, 0.97)",
          padding: "30px 20px 40px",
          borderRadius: "10px",
          maxWidth: "1000px",
          margin: "20px auto",
          color: "#333",
        }}
      >
        {/* Hero / title */}
        <Typography
          variant="h4"
          gutterBottom
          align="center"
          style={{ fontWeight: "bold", color: "#1E3A8A" }}
        >
          Data Protection & Security for Schools
        </Typography>
        <Typography
          variant="subtitle1"
          align="center"
          style={{ marginBottom: "20px", color: "#374151" }}
        >
          How YourStoryWorld protects pupil data, supports safeguarding, and
          complies with UK data protection law.
        </Typography>

        <Typography
          variant="body2"
          align="center"
          style={{ marginBottom: "30px", color: "#6B7280" }}
        >
          Data Controller: <strong>YOURSTORYWORLD LTD</strong> (Company
          No. <strong>17098564</strong>, UK)
        </Typography>

        {/* Summary cards */}
        <Grid container spacing={3} style={{ marginBottom: "30px" }}>
          <Grid item xs={12} md={4} data-aos="fade-up">
            <Card
              style={{
                height: "100%",
                borderRadius: "12px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
              }}
            >
              <CardContent>
                <LockIcon style={{ color: "#2563EB", fontSize: 30 }} />
                <Typography
                  variant="h6"
                  style={{ marginTop: "10px", fontWeight: "bold" }}
                >
                  Built for Schools
                </Typography>
                <Typography variant="body2">
                  Designed with UK schools in mind, with clear roles for school
                  admin, teachers and students, and no advertising or third-party
                  marketing to children.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4} data-aos="fade-up" data-aos-delay="100">
            <Card
              style={{
                height: "100%",
                borderRadius: "12px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
              }}
            >
              <CardContent>
                <SecurityIcon style={{ color: "#16A34A", fontSize: 30 }} />
                <Typography
                  variant="h6"
                  style={{ marginTop: "10px", fontWeight: "bold" }}
                >
                  Secure by Design
                </Typography>
                <Typography variant="body2">
                  Data is stored in secure cloud infrastructure with encryption
                  in transit (HTTPS) and at rest, and access is restricted to
                  authorised personnel only.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4} data-aos="fade-up" data-aos-delay="200">
            <Card
              style={{
                height: "100%",
                borderRadius: "12px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
              }}
            >
              <CardContent>
                <SchoolIcon style={{ color: "#F97316", fontSize: 30 }} />
                <Typography
                  variant="h6"
                  style={{ marginTop: "10px", fontWeight: "bold" }}
                >
                  Full School Control
                </Typography>
                <Typography variant="body2">
                  Schools can request exports, corrections, or deletion of pupil
                  data, and choose when to remove classes or entire school
                  accounts.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Section: What data we process */}
        <section style={{ marginBottom: "30px" }} data-aos="fade-right">
          <Typography variant="h5" style={{ fontWeight: "bold", marginBottom: 10 }}>
            What pupil and staff data do we process?
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 10 }}>
            When a school subscribes to YourStoryWorld, we only collect the
            minimum data needed to provide the service and learning analytics.
            Typical data we process includes:
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle1"
                style={{ fontWeight: "bold", marginTop: 10 }}
              >
                For school staff
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <AssessmentIcon style={{ color: "#2563EB" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Name, school email address and role (e.g. teacher, admin)"
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <AssessmentIcon style={{ color: "#2563EB" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="School / organisation name and basic subscription details"
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <AssessmentIcon style={{ color: "#2563EB" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Usage data (e.g. how many stories classes generate, reading trends)"
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
              </List>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle1"
                style={{ fontWeight: "bold", marginTop: 10 }}
              >
                For pupils / students
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <SchoolIcon style={{ color: "#16A34A" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="First name (or pseudonym), class name, and a login username"
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <SchoolIcon style={{ color: "#16A34A" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Story usage data: stories generated, reading time, quiz scores, voice recordings (if enabled)"
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <SchoolIcon style={{ color: "#16A34A" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Technical data such as device/browser type and IP address, for security and fraud prevention"
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </section>

        {/* Section: How and where we store data */}
        <section style={{ marginBottom: "30px" }} data-aos="fade-left">
          <Typography variant="h5" style={{ fontWeight: "bold", marginBottom: 10 }}>
            How and where is data stored?
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 10 }}>
            YourStoryWorld uses reputable cloud providers (such as Google
            Firebase) to host authentication, databases and file storage.
            Industry-standard safeguards are in place, including:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <LockIcon style={{ color: "#2563EB" }} />
              </ListItemIcon>
              <ListItemText
                primary="Encryption in transit via HTTPS for all app traffic"
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <LockIcon style={{ color: "#2563EB" }} />
              </ListItemIcon>
              <ListItemText
                primary="Encryption at rest for stored data provided by the cloud platform"
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon style={{ color: "#16A34A" }} />
              </ListItemIcon>
              <ListItemText
                primary="Strict access controls: only authorised administrators can access production data"
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon style={{ color: "#16A34A" }} />
              </ListItemIcon>
              <ListItemText
                primary="Audit trails and monitoring of core infrastructure"
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
          </List>
          <Typography variant="body2" style={{ marginTop: 10 }}>
            Data may be processed outside the UK/EEA where necessary using
            standard contractual safeguards and appropriate protections required
            under UK data protection law.
          </Typography>
        </section>

        {/* Section: Retention & deletion */}
        <section style={{ marginBottom: "30px" }} data-aos="fade-right">
          <Typography variant="h5" style={{ fontWeight: "bold", marginBottom: 10 }}>
            Data retention & deletion for schools
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 10 }}>
            We only keep pupil and school data for as long as it is needed to
            deliver the YourStoryWorld service, or as required by law. In
            practice this means:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <DeleteOutlineIcon style={{ color: "#DC2626" }} />
              </ListItemIcon>
              <ListItemText
                primary="If a school ends its subscription, we can remove student accounts and related data on request within a reasonable timeframe."
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <DeleteOutlineIcon style={{ color: "#DC2626" }} />
              </ListItemIcon>
              <ListItemText
                primary="Schools can archive or delete individual classes or students, and we will delete associated reading activity and recordings, subject to backups."
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <DeleteOutlineIcon style={{ color: "#DC2626" }} />
              </ListItemIcon>
              <ListItemText
                primary="Aggregated, fully anonymised usage statistics may be retained to help us improve the service and understand load, but this cannot be used to identify a pupil."
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
          </List>
        </section>

        {/* Section: Roles, DPAs & school responsibilities */}
        <section style={{ marginBottom: "30px" }} data-aos="fade-left">
          <Typography variant="h5" style={{ fontWeight: "bold", marginBottom: 10 }}>
            Roles, responsibilities & agreements
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 10 }}>
            For school subscriptions, YourStoryWorld typically acts as a{" "}
            <strong>data processor</strong> on behalf of the school, which
            remains the <strong>data controller</strong> for pupil data. In
            practice, this means:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <AssessmentIcon style={{ color: "#2563EB" }} />
              </ListItemIcon>
              <ListItemText
                primary="The school decides what pupil data to enter and how the platform is used in teaching."
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <AssessmentIcon style={{ color: "#2563EB" }} />
              </ListItemIcon>
              <ListItemText
                primary="YourStoryWorld processes data only to provide the service, support analytics and maintain security."
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <AssessmentIcon style={{ color: "#2563EB" }} />
              </ListItemIcon>
              <ListItemText
                primary="We do not sell pupil data, use it for targeted advertising, or share it with third parties for their own marketing."
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
          </List>
          <Typography variant="body2" style={{ marginTop: 10 }}>
            If your trust or local authority requires a formal{" "}
            <strong>Data Processing Agreement (DPA)</strong>, or a copy of our
            sub-processor list, please contact us at{" "}
            <a
              href="mailto:support@yourstoryworld.com"
              style={{ color: "#1D4ED8", textDecoration: "none" }}
            >
              support@yourstoryworld.com
            </a>
            .
          </Typography>
        </section>

        {/* Quick FAQ for DPOs */}
        <section style={{ marginBottom: "10px" }} data-aos="fade-up">
          <Typography variant="h5" style={{ fontWeight: "bold", marginBottom: 10 }}>
            Quick answers for Data Protection Leads
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <LockIcon style={{ color: "#2563EB" }} />
              </ListItemIcon>
              <ListItemText
                primary="Can you delete all data for our school if we leave?"
                secondary="Yes. On authorised request from the school, we can schedule deletion of pupil accounts and associated data, subject to backup and audit requirements."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <LockIcon style={{ color: "#2563EB" }} />
              </ListItemIcon>
              <ListItemText
                primary="Can you provide a data export for our records?"
                secondary="We can work with you to export pupil records and reading data in a structured format for your own archiving or analysis."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon style={{ color: "#16A34A" }} />
              </ListItemIcon>
              <ListItemText
                primary="How do you support safeguarding?"
                secondary="We provide teacher dashboards and activity logs so staff can see how pupils are using the platform. Our Child Safety & Safeguarding page explains how we minimise inappropriate content risk and handle reports."
              />
            </ListItem>
          </List>

          <Typography
            variant="body2"
            style={{ marginTop: 15, fontStyle: "italic", color: "#6B7280" }}
          >
            This page is provided for information only and does not constitute
            legal advice. Schools and trusts should review it alongside our
            Privacy Policy, Terms of Use, and their own data protection policies.
          </Typography>
        </section>
      </main>

      {/* Footer – re-use your policy links look/feel */}
      <footer
        style={{
          textAlign: "center",
          padding: "20px",
          backgroundColor: "#000",
          color: "#fff",
        }}
      >
        <Typography>
          &copy; {new Date().getFullYear()} YourStoryWorld (YOURSTORYWORLD LTD,
          Company No. 17098564) | All Rights Reserved
        </Typography>
        <Typography variant="body2" style={{ marginTop: 4 }}>
          <a href="/about" style={footerLinkStyle}>
            About Us
          </a>{" "}
          |{" "}
          <a href="/contact" style={footerLinkStyle}>
            Contact
          </a>{" "}
          |{" "}
          <a href="/help" style={footerLinkStyle}>
            Help & FAQs
          </a>
        </Typography>
        <Typography variant="body2" style={{ marginTop: 4 }}>
          <a href="/privacy-policy" style={footerLinkStyle}>
            Privacy Policy
          </a>{" "}
          |{" "}
          <a href="/terms" style={footerLinkStyle}>
            Terms of Use
          </a>{" "}
          |{" "}
          <a href="/cookie-policy" style={footerLinkStyle}>
            Cookie Policy
          </a>{" "}
          |{" "}
          <a href="/child-safety" style={footerLinkStyle}>
            Child Safety
          </a>{" "}
          |{" "}
          <a href="/accessibility" style={footerLinkStyle}>
            Accessibility
          </a>{" "}
          |{" "}
          <a href="/data-protection" style={footerLinkStyle}>
            Data Protection &amp; Security
          </a>
        </Typography>
      </footer>
    </div>
  );
};

const footerLinkStyle = {
  color: "white",
  textDecoration: "none",
};

export default DataProtectionSchools;
